//#region IMPORTS
import { UPLOAD_IMAGES_FOR_PRODUCT_VERSIONS } from "../../models/_consts";
import { closeModal, openModal } from "../../features/dashBoard/dashboardSlice";
import { downloadErrorsExcel } from "../../helpers/downloadErrorsExcel";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { ImageUpload, ImageUploadResult } from "../../models/image";
import { ArrowUpCircleIcon } from "@heroicons/react/20/solid";
import { useAppDispatch } from "../../store/configureStore";
import { toast } from "react-toastify";
import ActionButton from "../genericButtons/actionButton/ActionButton";
import LoadingData from "../loading/LoadingData";
import MainModal from "../_layout/MainModal";
import agent from "../../api/agent";
//#endregion

//#region INTERFACE
interface Props {
  disabled?: boolean;
}

interface Progress {
  fileName: string;
  index: number;
  total: number;
}
//#endregion

// The whole API sits behind a rate limit of 60 requests per minute per user.
// We pace the uploads just under that ceiling so the rest of the app keeps some
// room, and we still recover if we do hit the limit.
const REQUEST_SPACING_MS = 1200;
const RATE_LIMIT_STATUS = 429;
const MAX_RATE_LIMIT_WAITS = 10;
// A 5xx or a dropped connection is worth another try, but the cost differs
// wildly: a gateway timeout burns 60s before it answers, while a reset from the
// load balancer ("upstream connect error", no headers) comes back in half a
// second. So a retry is bounded by both a count and the time this one file has
// already cost — the timeout case stops after one retry, the cheap case gets
// all four. Failing fast still matters: if the backend is simply down, every
// file in the batch pays this.
const ERROR_RETRY_BUDGET_MS = 90000;
const ERROR_BACKOFF_STEP_MS = 2000;
const MAX_ERROR_RETRIES = 4;
// Used when the API does not tell us how long to wait (the Retry-After header
// is not exposed to the browser on cross origin calls).
const FALLBACK_RETRY_AFTER_SECONDS = 60;

const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, Math.max(ms, 0)));

// Turn an axios error into a message the user can act on.
const describeError = (error: any): string => {
  const apiMessage = error?.response?.data?.error;
  if (apiMessage) return apiMessage;

  const status = error?.response?.status;
  if (status === RATE_LIMIT_STATUS)
    return "The API rate limit kept blocking this file. Upload it again later.";
  if (status) return `Upload failed (HTTP ${status}).`;

  return error?.message ?? "Upload failed for an unknown reason.";
};

function ImageUploaderV3({ disabled = false }: Props) {
  //#region SETUP
  // Redux
  const dispatch = useAppDispatch();

  // Local state
  const [productFileGroups, setProductFileGroups] = useState<ImageUpload[]>([]);
  const [results, setResults] = useState<ImageUploadResult[]>([]);
  const [rateLimitWait, setRateLimitWait] = useState<number | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [productNames, setProductNames] = useState<string[]>([]);
  const [savingBatch, setSavingBatch] = useState<boolean>(false);
  const [loadedFiles, setLoadedFiles] = useState<File[]>([]);
  const [finished, setFinished] = useState<boolean>(false);
  const [ready, setReady] = useState<boolean>(false);

  // A cancel has to reach the running upload loop, not just the render.
  const cancelled = useRef<boolean>(false);
  //#endregion

  //#region LOGIC
  //#region PREPARE DATA
  // Upload handler
  const getFiles = async (event: any) => {
    const files = await event.target.files;
    setLoadedFiles([...files]);
  };

  // Make product groups
  useEffect(() => {
    const products = [];
    const map = new Map();
    for (const item of loadedFiles) {
      if (!map.has(item.name.split("_")[0])) {
        map.set(item.name.split("_")[0], true);
        products.push(item.name.split("_")[0]);
      }
    }
    setProductNames(products);
  }, [loadedFiles]);

  // Group versions by product
  useEffect(() => {
    const productVersionFiles: ImageUpload[] = [];
    productNames.forEach((productName) => {
      productVersionFiles.push({
        product: productName,
        versions: loadedFiles.filter(
          (fileDeep) => fileDeep.name.split("_")[0] === productName
        ),
        saved: false,
      });
      setProductFileGroups(productVersionFiles);
    });
  }, [productNames, loadedFiles]);

  // Ready watcher
  useEffect(() => {
    loadedFiles.length > 0 && setReady(true);
  }, [loadedFiles]);
  //#endregion

  //#region REPORT
  const resultsByFileName = useMemo(
    () => new Map(results.map((result) => [result.fileName, result])),
    [results]
  );

  const failedResults = useMemo(
    () => results.filter((result) => !result.ok),
    [results]
  );

  // Everything that did not make it: failed files plus files a cancel never
  // got around to. These are the ones a retry has to cover.
  const pendingFiles = useMemo(
    () => loadedFiles.filter((file) => !resultsByFileName.get(file.name)?.ok),
    [loadedFiles, resultsByFileName]
  );

  const succeededCount = results.length - failedResults.length;
  const notAttemptedCount = pendingFiles.length - failedResults.length;

  // Progress within the product the current file belongs to, so a promo with
  // sixteen versions of one product still reads as "4 / 16".
  const currentGroup = useMemo(() => {
    if (!progress) return null;

    const product = progress.fileName.split("_")[0];
    const group = productFileGroups.find((entry) => entry.product === product);
    if (!group) return null;

    const done = group.versions.filter((version) =>
      resultsByFileName.has(version.name)
    ).length;

    return { product, done, total: group.versions.length };
  }, [progress, productFileGroups, resultsByFileName]);

  // Rows for the error spreadsheet, in the order the files were picked.
  const errorRows = useMemo(
    () =>
      pendingFiles.map((file) => {
        const result = resultsByFileName.get(file.name);

        return {
          File: file.name,
          Status: result ? "Failed" : "Not attempted",
          Reason: result?.message ?? "The batch was cancelled before this file.",
        };
      }),
    [pendingFiles, resultsByFileName]
  );

  const downloadErrorsHandler = () =>
    downloadErrorsExcel(errorRows, "product-version-image-upload-errors");
  //#endregion

  //#region MODAL
  const openUploaderHandler = () =>
    dispatch(openModal(UPLOAD_IMAGES_FOR_PRODUCT_VERSIONS));

  // Clear
  const clearUploadHandler = () => {
    cancelled.current = false;
    setProductFileGroups([]);
    setRateLimitWait(null);
    setProductNames([]);
    setSavingBatch(false);
    setLoadedFiles([]);
    setProgress(null);
    setFinished(false);
    setResults([]);
    setReady(false);
  };

  // The modal unmounts its children, so the file input is a fresh one on every
  // open. That matters: an input that keeps its value does not fire onChange
  // when you pick the very same folder again.
  const closeUploaderHandler = () => {
    clearUploadHandler();
    dispatch(closeModal());
  };

  // Stop the running batch, but keep the results so far on screen.
  const cancelUploadHandler = () => {
    cancelled.current = true;
  };
  //#endregion

  //#region UPLOAD
  // Sit out the rate limit window. Works from a deadline instead of counting
  // timers down, so a backgrounded tab (where browsers slow timers down) still
  // resumes on time.
  const waitForRateLimit = async (seconds: number) => {
    const deadline = Date.now() + seconds * 1000;

    while (Date.now() < deadline && !cancelled.current) {
      setRateLimitWait(Math.ceil((deadline - Date.now()) / 1000));
      await sleep(Math.min(1000, deadline - Date.now()));
    }

    setRateLimitWait(null);
  };

  // Save one file, retrying a rate limit or a temporary server error instead of
  // dropping the file silently.
  const saveOne = async (file: File): Promise<ImageUploadResult> => {
    const maxTries = MAX_RATE_LIMIT_WAITS + MAX_ERROR_RETRIES + 1;
    let rateLimitWaits = 0;
    let errorRetries = 0;
    let errorTimeMs = 0;

    for (let round = 0; round < maxTries; round++) {
      const formDataFile = new FormData();
      formDataFile.append("file", file);
      const startedAt = Date.now();

      try {
        await agent.ProductVersions.image(formDataFile);
        return { fileName: file.name, ok: true };
      } catch (error: any) {
        const status = error?.response?.status;

        // Rate limited: wait as long as the API asks, then try the same file
        // again. Does not spend the error budget.
        if (
          status === RATE_LIMIT_STATUS &&
          rateLimitWaits < MAX_RATE_LIMIT_WAITS
        ) {
          rateLimitWaits += 1;
          const retryAfter = Number(error?.response?.headers?.["retry-after"]);

          await waitForRateLimit(
            Number.isFinite(retryAfter) && retryAfter > 0
              ? retryAfter + 1
              : FALLBACK_RETRY_AFTER_SECONDS
          );

          if (cancelled.current)
            return { fileName: file.name, ok: false, message: "Cancelled." };
          continue;
        }

        // Network hiccup or server error: worth another attempt as long as
        // this file has budget left.
        if (
          (status === undefined || status >= 500) &&
          errorRetries < MAX_ERROR_RETRIES
        ) {
          const backoff = (errorRetries + 1) * ERROR_BACKOFF_STEP_MS;
          errorTimeMs += Date.now() - startedAt + backoff;

          if (errorTimeMs < ERROR_RETRY_BUDGET_MS) {
            errorRetries += 1;
            await sleep(backoff);
            if (cancelled.current)
              return { fileName: file.name, ok: false, message: "Cancelled." };
            continue;
          }
        }

        return { fileName: file.name, ok: false, message: describeError(error) };
      }
    }

    return {
      fileName: file.name,
      ok: false,
      message: "Upload failed after several attempts.",
    };
  };

  // Save a list of files one by one, keeping a result for every single file.
  const runUpload = async (files: File[]) => {
    cancelled.current = false;
    setSavingBatch(true);
    setFinished(false);

    // Forget what we knew about the files in this run. A retry otherwise keeps
    // showing the previous run's numbers — "12 failed so far" while only four
    // have been retried, and a product sitting at "11 / 11 files" — because
    // every old result is still there until its replacement lands. Results of
    // files that are not in this run (the ones that already succeeded) stay.
    const namesInRun = new Set(files.map((file) => file.name));
    setResults((previous) =>
      previous.filter((entry) => !namesInRun.has(entry.fileName))
    );

    // Counted locally as well: the toast should not depend on when React
    // catches up with the state updates.
    const collected: ImageUploadResult[] = [];

    for (let index = 0; index < files.length; index++) {
      if (cancelled.current) break;

      const file = files[index];
      setProgress({
        fileName: file.name,
        index: index + 1,
        total: files.length,
      });

      const result = await saveOne(file);
      collected.push(result);
      setResults((previous) => [
        ...previous.filter((entry) => entry.fileName !== result.fileName),
        result,
      ]);

      if (index < files.length - 1) await sleep(REQUEST_SPACING_MS);
    }

    const stopped = cancelled.current;
    const failedCount = collected.filter((entry) => !entry.ok).length;

    setRateLimitWait(null);
    setSavingBatch(false);
    setProgress(null);
    setFinished(true);

    if (stopped)
      toast.warning(`Upload cancelled after ${collected.length} files`);
    else if (failedCount === 0)
      toast.success(`${collected.length} files uploaded`);
    else toast.error(`${failedCount} of ${collected.length} files failed`);
  };

  // Save all loaded files
  const saveProductImagesHandler = () => runUpload(loadedFiles);

  // Save what did not make it, without picking the files from disk again.
  const retryPendingHandler = () => runUpload(pendingFiles);
  //#endregion
  //#endregion

  //#region RENDER
  return (
    <Fragment>
      <ActionButton
        clickHandler={openUploaderHandler}
        disabled={disabled}
        value={"Upload images"}
      >
        <ArrowUpCircleIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
      </ActionButton>

      <MainModal
        type={UPLOAD_IMAGES_FOR_PRODUCT_VERSIONS}
        closeHandler={closeUploaderHandler}
        closeDisabled={savingBatch}
      >
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-base font-semibold leading-6 text-gray-900">
            {savingBatch
              ? "Uploading product version images"
              : "Upload product version images"}
          </h3>

          {/* NOTHING PICKED YET */}
          {!ready && (
            <Fragment>
              <div className="mt-2 max-w-xl text-sm text-gray-500">
                <p>
                  A file is matched on its name without extension, which has to
                  be the alternation number of the product version.
                </p>
              </div>

              <div className="mt-5">
                <input
                  className="block w-full text-sm text-gray-900 focus:outline-none"
                  onChange={getFiles}
                  type="file"
                  name="file"
                  multiple
                  autoFocus
                />
              </div>
            </Fragment>
          )}

          {/* PICKED, NOT STARTED */}
          {ready && !savingBatch && !finished && (
            <Fragment>
              <p className="mt-2 text-sm text-gray-500">
                {loadedFiles.length} files across {productFileGroups.length}{" "}
                products
              </p>

              <div className="mt-5 flex items-center gap-4">
                <ActionButton
                  clickHandler={saveProductImagesHandler}
                  value={"Save all"}
                  hidePlusIcon
                />
                <button
                  className="text-sm font-semibold leading-6 text-gray-900"
                  onClick={closeUploaderHandler}
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </Fragment>
          )}

          {/* RUNNING */}
          {savingBatch && progress && (
            <Fragment>
              {/* LoadingData is a 64px box rotated 45 degrees, so it draws
                  about 90px and spills out of the space it reserves. Hence a
                  fixed height that fits the rotated box, plus real margins. */}
              <div className="my-8 flex h-24 items-center justify-center">
                <LoadingData />
              </div>

              <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full rounded-full bg-emerald-600 transition-all"
                  style={{
                    width: `${Math.round(
                      (progress.index / progress.total) * 100
                    )}%`,
                  }}
                />
              </div>

              <p className="mt-2 text-sm font-semibold text-gray-900">
                {progress.index} / {progress.total} files
              </p>

              {currentGroup && (
                <p className="text-sm text-gray-500">
                  {currentGroup.product} · {currentGroup.done} /{" "}
                  {currentGroup.total} files
                </p>
              )}

              <p className="truncate text-xs text-gray-400">
                {progress.fileName}
              </p>

              {failedResults.length > 0 && (
                <p className="mt-1 text-sm font-semibold text-red-600">
                  {failedResults.length} failed so far
                </p>
              )}

              {rateLimitWait !== null && (
                <p className="mt-3 border-l-4 border-amber-400 bg-amber-50 p-2 text-sm text-gray-700">
                  API limit reached, resuming in {rateLimitWait}s. Nothing is
                  lost, keep this window open.
                </p>
              )}

              <div className="mt-5">
                <button
                  className="text-sm font-semibold leading-6 text-gray-900"
                  onClick={cancelUploadHandler}
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </Fragment>
          )}

          {/* DONE */}
          {finished && (
            <Fragment>
              <p className="mt-2 text-sm text-gray-900">
                <b>{succeededCount}</b> uploaded, <b>{failedResults.length}</b>{" "}
                failed
                {notAttemptedCount > 0 && (
                  <Fragment>
                    , <b>{notAttemptedCount}</b> not attempted
                  </Fragment>
                )}
              </p>

              {failedResults.length > 0 && (
                <ul className="mt-3 max-h-64 divide-y divide-gray-100 overflow-y-auto">
                  {failedResults.map((result) => (
                    <li
                      className="flex items-start justify-between gap-4 py-1"
                      key={result.fileName}
                    >
                      <span className="text-xs text-gray-900">
                        {result.fileName}
                      </span>
                      <span className="text-right text-xs text-red-600">
                        {result.message}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-5 flex items-center gap-4">
                {pendingFiles.length > 0 && (
                  <ActionButton
                    clickHandler={retryPendingHandler}
                    value={`Retry ${pendingFiles.length} remaining`}
                    hidePlusIcon
                  />
                )}

                {errorRows.length > 0 && (
                  <ActionButton
                    className="bg-gray-600 hover:bg-gray-500 focus-visible:outline-gray-600"
                    clickHandler={downloadErrorsHandler}
                    value={"Download errors"}
                    hidePlusIcon
                  />
                )}

                <button
                  className="text-sm font-semibold leading-6 text-gray-900"
                  onClick={closeUploaderHandler}
                  type="button"
                >
                  Done
                </button>
              </div>
            </Fragment>
          )}
        </div>
      </MainModal>
    </Fragment>
  );
  //#endregion
}

export default ImageUploaderV3;
